<template>
  <div class="InsertBlock">
    <Entities renderless :collection="insightsCollection" v-model="insights" />

    <div>
      <a-dropdown :trigger="['click']" placement="topRight">
        <template v-if="$slots.default">
          <slot />
        </template>
        <a-button v-else type="primary" icon="plus">New</a-button>
        <a-menu slot="overlay">
          <template v-for="option in options">
            <a-sub-menu v-if="option.items" :key="option.key">
              <span slot="title">{{ option.name }}</span>
              <a-menu-item
                v-for="item in option.items"
                :key="item.key"
                @click="option.itemClick(option, item)"
              >
                {{ item.name }}
              </a-menu-item>
            </a-sub-menu>

            <a-menu-item v-else :key="option.key" @click="option.click(option)">
              <a-icon :type="option.icon" />
              {{ option.name }}
            </a-menu-item>
            <a-menu-divider v-if="option.divide" :key="`_${option.key}`" />
          </template>
          <template v-if="!hideBlocks">
            <a-menu-divider />
            <a-menu-item key="journey" @click="newJourneyModal = true">
              <a-icon type="plus" />
              New User Journey
            </a-menu-item>
          </template>
        </a-menu>
      </a-dropdown>
      <a-modal v-model="newBlockModal" title="Create a new Block" @ok="addNewBlock">
        <template v-for="({ label, placeholder }, key) in addNewBlockFields">
          <div :key="`_${key}`">
            {{ label }}
          </div>
          <a-input :key="key" v-bind="{ placeholder }" v-model="modalForm[key]" />
        </template>
      </a-modal>
      <a-modal v-model="newJourneyModal" title="Create a new Journey" @ok="addNewJourney">
        <a-select
          mode="multiple"
          placeholder="Select all blocks in this journey"
          style="width: 100%"
          v-model="selectedBlockIds"
        >
          <a-select-option v-for="block in blocks" :key="block.id" :value="block.id">
            {{ block.content.title }}
          </a-select-option>
        </a-select>
      </a-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Firebase from '@firebase/app'
import '@firebase/firestore'

import { getRootPath, getPageId } from '@/helpers/path'
import { getPresetConfigs } from '@/components/dashboard/insightCardPresets'
import { cloneDeep } from 'lodash'

export default {
  name: 'InsertBlock',
  components: {},
  inject: {
    _editingMode: {
      default: null,
    },
  },
  props: {
    currentPath: String,
    mini: Boolean,
    hideBlocks: Boolean,
    hideInsights: Boolean,
    blocks: Object,
  },
  data() {
    return {
      insights: [],
      items: [],
      formPageData: {},
      layout: null,
      submission: {},
      journeys: [],
      newJourneyModal: false,
      selectedBlockIds: [],
      addingNewBlockKey: false,
      modalForm: {},
    }
  },
  computed: {
    ...mapGetters(['activeGroupId', 'isAdmin', 'flows']),
    ...mapState(['allFlows']),
    insightsCollection() {
      return this.projectId
        ? `projects/${this.projectId}/stats`
        : `groups/${this.$store.getters.activeGroupId}/insights`
    },
    projectId() {
      return this.$route.params.projectId
    },
    pageId() {
      return getPageId(this.path)
    },
    rootPath() {
      return getRootPath(this.path)
    },
    path() {
      return this.currentPath
    },
    presetInsights() {
      return getPresetConfigs()
    },
    options() {
      const options = [
        {
          key: 'duplicate-flow',
          name: 'Duplicate Flow',
          icon: 'plus',
          items: this.flows.map(d => {
            return { key: d.id, name: d.title, icon: 'plus' }
          }),
          itemClick: this.duplicateFlow,
        },
      ]
      if (!this.hideInsights) {
        options.unshift(
          {
            key: 'new-insight',
            name: 'Create new metric card',
            icon: 'plus',
            click: this.addCard,
          },
          {
            key: 'existing-insight',
            name: 'Add Existing Metric Card',
            icon: 'plus',
            items: this.insights.map(d => {
              return { key: d.id, name: d.title, icon: 'plus' }
            }),
            itemClick: this.addExistingInsight,
          },
          {
            key: 'preset-insight',
            name: 'Add Preset Metric Cards',
            icon: 'plus',
            items: Object.entries(this.presetInsights).map(([key, d]) => {
              return { key, name: d.title, icon: 'plus' }
            }),
            itemClick: this.addCard,
          }
        )
      }
      if (!this.hideBlocks)
        options.push(
          {
            key: 'new-block',
            name: 'Create new block',
            icon: 'plus',
            items: this.newBlocks,
            itemClick: this.beginAddNewBlock,
          },
          {
            key: 'existing-block',
            icon: 'plus',
            name: 'Add Existing Block',
            items: [],
          }
        )
      return options
    },
    newBlocks() {
      return [
        {
          key: 'blank',
          name: 'Blank',
          icon: 'plus',
          divide: true,
        },
        {
          key: 'website',
          name: 'Website',
          icon: 'global',
          fields: {
            domain: {
              label: 'What domain is this website on?',
              placeholder: 'example.com',
            },
          },
        },
        {
          key: 'webpage',
          name: 'Webpage',
          icon: 'file',
          fields: {
            page: {
              label: `What's the URL of this webpage?`,
              placeholder: 'example.com/pricing',
            },
          },
        },
        {
          key: 'webapp',
          name: 'Webapp',
          icon: 'gold',
          fields: {
            domain: {
              label: 'What domain is this webapp on?',
              placeholder: 'app.example.com',
            },
          },
          divide: true,
        },
        {
          key: 'organic',
          name: 'Organic / SEO',
          icon: 'compass',
        },
        {
          key: 'email',
          name: 'Email',
          icon: 'mail',
        },
        {
          key: 'referral',
          name: 'Referrals',
          icon: 'sync',
          divide: true,
        },
        {
          key: 'google',
          name: 'Google Ads',
          icon: 'google',
        },
        {
          key: 'facebook',
          name: 'Facebook Ads',
          icon: 'facebook',
        },
        {
          key: 'instagram',
          name: 'Instagram Ads',
          icon: 'instagram',
        },
        {
          key: 'twitter',
          name: 'Twitter Ads',
          icon: 'twitter',
        },
        {
          key: 'reddit',
          name: 'Reddit Ads',
          icon: 'reddit',
        },
        {
          key: 'quora',
          name: 'Quora Ads',
          icon: 'question-circle',
        },
      ]
    },
    newBlockModal: {
      get() {
        return Boolean(this.addingNewBlockKey)
      },
      set(val) {
        this.addingNewBlockKey = val
      },
    },
    addNewBlockFields() {
      const newBlock = this.newBlocks.find(b => b.key === this.addingNewBlockKey)
      return newBlock && newBlock.fields
    },
  },
  watch: {},
  methods: {
    async addCard(option, item) {
      const collection = this.insightsCollection
      let data = {}
      if (item) {
        data = { ...(this.presetInsights[item.key] || {}) }
      }

      const ref = await Firebase.firestore()
        .collection(collection)
        .add(data)

      this.addInsightCard(ref.id)
    },
    addInsightCard(id) {
      this.$emit('insight-added', id)
    },
    addExistingInsight(option, item) {
      this.addInsightCard(item.key)
    },
    beginAddNewBlock(option, item) {
      const preset = item.key
      const fields = item.fields
      if (fields) this.addingNewBlockKey = preset
      else this.addNewBlock(preset)
    },
    addNewBlock(preset) {
      if (typeof preset !== 'string') preset = this.addingNewBlockKey
      const data = this.getBlockPresetData(
        this.currentPath.split('/').slice(-1)[0],
        preset,
        this.modalForm
      )

      Firebase.firestore()
        .collection(`content/${this.rootPath}/pages`)
        .add(data)

      this.addingNewBlockKey = null
      this.modalForm = {}
    },
    addNewJourney() {
      const ref = Firebase.firestore()
        .collection(`content/${this.rootPath}/journeys`)
        .doc()
      const data = {
        pageId: this.pageId,
        blockIds: this.selectedBlockIds.sort(
          (a, b) => this.blocks[a].content.grid.x - this.blocks[b].content.grid.x
        ),
      }

      ref.set(data)

      this.selectedBlockIds = []

      this.newJourneyModal = false
    },
    getBlockPresetData(parentPageId, preset, fieldAnswers) {
      return {
        parentPageId,
        layout: 'builder',
        title: `Block ${Object.values(this.blocks).length + 1}`,
        grid: {
          w: 4,
          h: 6,
          x: 0,
          y: 0,
          i: Object.values(this.blocks).length,
        },
        ...getSpecialBlockPresetData(preset, fieldAnswers),
      }
    },
    async duplicateFlow(option, item) {
      const flow = this.flows.find(f => f.id === item.key)
      const id = await this.dupeFlow(flow, this.activeGroupId, `Copy of ${flow.title}`)
      this.$router.push(`/project/flows/${id}`)
    },
    async dupeFlow(flow, groupId, title) {
      const newDoc = Firebase.firestore()
        .collection('forms')
        .doc()
      const newFlow = cloneDeep(flow)
      delete newFlow._meta
      const data = {
        form: JSON.stringify({ ...newFlow, id: newDoc.id, title }),
        groupId,
        version: 0,
        isLive: false,
        createdAt: new Date().toISOString(),
        // builder_version: 2,
      }
      await newDoc.set(data)
      return newDoc.id
    },
  },
}
function getSpecialBlockPresetData(preset, fieldAnswers) {
  switch (preset) {
    case 'website':
      return {
        // backgroundImage:
        //   'https://screenshots.webflow.com/sites/5f122a50a4e3e6f47b120f7e/20200820234109_0658acd64e72df72507e1a46fd1544f8.png',
        tag: 'Marketing Site',
        title: fieldAnswers.domain,
        cleanUrl: fieldAnswers.domain,
        filters: {
          domain: fieldAnswers.domain,
        },
        'service-icon': '/images/icons/webflow.png',
      }
    case 'webpage':
      return {
        backgroundImage:
          'https://screenshots.webflow.com/sites/5f122a50a4e3e6f47b120f7e/20200820234109_0658acd64e72df72507e1a46fd1544f8.png',
        tag: 'Marketing Site',
        title: fieldAnswers.page,
        cleanUrl: fieldAnswers.page,
        filters: {
          page: fieldAnswers.page,
        },
        'service-icon': '/images/icons/webflow.png',
      }
    case 'webapp':
      return {
        backgroundImage:
          'https://media.sproutsocial.com/uploads/2018/04/Twitter-Analytics-Tools.png',
        tag: 'Web App',
        title: fieldAnswers.domain,
        cleanUrl: fieldAnswers.domain,
        filters: {
          domain: fieldAnswers.domain,
        },
        'service-icon': '/images/icons/code.png',
      }
    case 'organic':
      return {
        backgroundImage: '',
        tag: 'Organic',
        title: 'Organic / SEO',
        filters: {
          channel: '__null',
        },
        'service-icon':
          'data:image/png;base64,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',
      }
    case 'email':
      return {
        backgroundImage: '',
        tag: 'Outbound',
        title: 'Email',
        filters: {
          medium: 'email',
        },
        'service-icon': 'https://freeiconshop.com/wp-content/uploads/edd/mail-open-flat.png',
      }
    case 'referral':
      return {
        backgroundImage: '',
        tag: 'Referrals',
        title: 'Referrals',
        filters: {
          medium: 'referral',
        },
        'service-icon':
          'https://e7.pngegg.com/pngimages/694/843/png-clipart-referral-marketing-computer-icons-graphics-illustration-affiliate-icon-logo-social-media-marketing.png',
      }
    case 'google':
      return {
        backgroundImage:
          'data:image/jpeg;base64,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',
        tag: 'Ads',
        title: 'Google Ads',
        'service-icon': '/images/icons/google.svg',
        filters: {
          channel: 'google-ads',
        },
      }
    case 'facebook':
      return {
        backgroundImage:
          'https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcSFATeMtABAX70Vu7n2emprOtgSR_eYtiVZ1A&usqp=CAU',
        tag: 'Ads',
        title: 'Facebook Ads',
        'service-icon': '/images/icons/facebook.png',
        filters: {
          channel: 'facebook-ads',
        },
      }
    case 'instagram':
    case 'twitter':
    case 'reddit':
    case 'quora':
      return {
        // backgroundImage:
        //   'https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcSFATeMtABAX70Vu7n2emprOtgSR_eYtiVZ1A&usqp=CAU',
        tag: 'Ads',
        title: `${preset[0].toUpperCase() + preset.slice(1)} Ads`,
        'service-icon': `/images/icons/${preset}.png`,
        filters: {
          channel: `${preset}-ads`,
        },
      }
    default:
      return null
  }
}
</script>
